import React, { useEffect, useState } from "react";
import { Pause, AlertTriangle, ChevronRight } from "lucide-react";
import { api } from "../lib/api";

/**
 * PausedAthletesAlert — dashboard banner that surfaces temporarily
 * paused athletes so coaches don't forget about them.
 *
 * Two states:
 *   • Any paused → show a compact card ("N paused athletes")
 *   • Any overdue → escalate to amber/red and list overdue names
 *
 * When zero paused, renders nothing — the alert only exists to remind
 * coaches about athletes they've explicitly deprioritised.
 *
 * The `onOpenAthlete` callback flips the top-level tab to the athlete
 * view so a coach can jump straight from "who's overdue?" to "call
 * this athlete's parent".
 */
export default function PausedAthletesAlert({ onOpenAthlete }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/sailors/paused-summary")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);

  if (!data || !data.paused) return null;
  const overdue = data.overdue_items || [];
  const isOverdue = overdue.length > 0;

  return (
    <div
      className="card p-4 md:p-5 border"
      style={{
        borderColor: isOverdue ? "rgba(239,68,68,0.45)" : "rgba(14,165,233,0.35)",
        background: isOverdue
          ? "linear-gradient(135deg, rgba(239,68,68,0.08), rgba(11,22,39,0.6))"
          : "linear-gradient(135deg, rgba(14,165,233,0.08), rgba(11,22,39,0.6))",
      }}
      data-testid="paused-athletes-alert"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: isOverdue ? "rgba(239,68,68,0.15)" : "rgba(14,165,233,0.15)" }}
          >
            {isOverdue ? <AlertTriangle size={16} className="text-rose-300" /> : <Pause size={16} className="text-sky-300" />}
          </div>
          <div>
            <div className="font-display text-base text-slate-100">
              {data.paused} paused athlete{data.paused === 1 ? "" : "s"}
              {isOverdue && (
                <span className="ml-2 text-[11px] font-mono-data px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-200 border border-rose-500/40">
                  {data.overdue} overdue
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">
              {isOverdue
                ? "These athletes were expected back — check in on them."
                : "On temporary pause. Hidden from KPIs & briefings."}
            </div>
          </div>
        </div>
      </div>

      {isOverdue && (
        <div className="mt-3 border-t border-[#1E304B] pt-3 space-y-1.5 max-h-48 overflow-y-auto">
          {overdue.slice(0, 12).map((it) => (
            <button
              key={it.sailor_id}
              onClick={() => onOpenAthlete?.(it.sailor_id)}
              className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg hover:bg-[#0E1A2E] transition text-left"
              data-testid={`paused-overdue-row-${it.sailor_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-100 truncate">{it.name}</div>
                <div className="text-[10px] text-slate-500 font-mono-data truncate">
                  {it.group}{it.subgroup ? ` · ${it.subgroup}` : ""}
                  {it.reason && <span> · {it.reason}</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[11px] font-mono-data text-rose-300">
                  {it.days_overdue}d overdue
                </div>
                <div className="text-[10px] text-slate-600 font-mono-data">
                  was {it.expected_return_date}
                </div>
              </div>
              <ChevronRight size={12} className="text-slate-500 shrink-0" />
            </button>
          ))}
          {overdue.length > 12 && (
            <div className="text-[10px] text-slate-500 text-center pt-1">
              +{overdue.length - 12} more overdue
            </div>
          )}
        </div>
      )}
    </div>
  );
}
